import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 4.4 visual preview at /preview/lesson/4.4
 * "Defend what you shipped: the standing move"
 */

export default function LessonPreviewPage4_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 4 · Doing the work AI cannot do · Lesson 4.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Defend what you shipped: the standing move
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Judgment · Character · Chain</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 defense drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Same essay. One question. Two students. Two futures.">
      <p className="mt-6 text-text/90">
        Two students hand in the same kind of essay on the same week. The
        teacher reads both, picks one paragraph from each, walks to each desk,
        and asks: where did you get this number?
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Student A · Cannot answer
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;The article said it. I do not remember which one.&rdquo;
          </p>
          <p className="mt-4 text-text/80">
            The teacher writes a note. The teacher is not yet angry. The teacher
            is now watching. Every paragraph the student writes for the rest of
            the year will get read with one extra question in the teacher&apos;s
            mind.
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Student B · Can answer
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;The 38% figure is from the Champlain College 2023 financial
            literacy report card, page 4. I read the page. The number is for
            states with a required half-credit course.&rdquo;
          </p>
          <p className="mt-4 text-text/80">
            The teacher nods, walks away, and moves on. The student has just
            shown the teacher who they are without saying it. Every paragraph the
            student writes from now on starts with an inch of credit already in
            the room.
          </p>
        </div>
      </div>
      <p className="mt-6 text-text/90">
        Same essay. Same number. Same time spent writing. The student who can
        defend the work is treated differently for the rest of the school year.
        That is not punishment. That is how trust works.
      </p>
      <p className="mt-4 text-text/90">
        The chain in Week 3 ends with ship. This lesson is what ship actually
        means: you can stand behind every claim and every paragraph the moment
        someone asks.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lessons 3.4 and 4.3" title="Shippable then. Defensible now.">
      <p className="mt-6 text-text/90">
        Two threads meet here. From 3.4: a shippable draft is one you would defend
        if a teacher asked you to explain a paragraph. From 4.3: a defensible
        paragraph has particulars only you have. Both lead to today.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Provenance in one sentence." />
        <ReviewItem n="02" q="Define Particular in one sentence." />
        <ReviewItem n="03" q="Why does AI struggle with both of those, in one sentence?" />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The standing move" title="What it means to stand behind your work, and the kit that makes it possible.">
      <Subhead n={1} title="The vocab" />
      <p className="mt-4 text-text/90">
        There is a word for work you can stand behind. It is not the same as
        correct. It is not the same as shippable. It is the next layer up.
      </p>
      <VocabCard
        term="Auditability"
        definition="The property of work that lets anyone, including you an hour later, trace every claim to its source and every AI-assisted edit to a reason you accepted it. Auditable work survives the question &ldquo;why is that sentence there?&rdquo; three times in a row. It is not built after the fact. It is built while you work."
        analogy="An auditable expense report is not the trip. It is the receipts behind the trip. If someone asks why a number is there, you can point at the receipt in ten seconds. Auditable writing works the same way. The work is the trip. The defense kit is the receipts. No receipts, no defense."
        memoryHook="True. Yours. Traceable. Three drawers. If one is empty, the work is not auditable yet."
        why="Shipping is the moment you submit. Auditability is what protects you the day someone pulls you back to it. In school it is academic integrity. At work it is the question your boss asks when a client pushes back. In civic life it is whether your letter holds up under cross-examination. The defense kit is what makes the work auditable in the first place."
      />

      <Subhead n={2} title="The defense kit" className="mt-12" />
      <p className="mt-4 text-text/90">
        Three pieces of paper in your notebook. Or three columns in a doc. You
        keep them while you work, not after. After the work is done they take an
        hour to build. While the work is happening, they take seconds.
      </p>
      <KitGrid />

      <Subhead n={3} title="The three questions a teacher will ask" className="mt-12" />
      <p className="mt-4 text-text/90">
        Teachers do not ask twelve questions to test your defense. They ask
        three. Every other question is a variation of one of these. Be ready for
        all three on every paragraph.
      </p>
      <QuestionsGrid />

      <Subhead n={4} title="The rule that keeps the chain honest" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI suggestions are not bad. AI sentences in a final draft are not bad.
        Pasting an AI sentence you cannot explain is bad. There is one rule that
        sorts the three.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The pass-through rule
        </p>
        <p className="mt-3 text-text/90">
          Every AI sentence that ends up in the final draft has to pass through
          your brain on the way in. Pass-through means: you read it out loud, you
          decide to keep it, and you re-type it by hand. If you pasted it, you
          did not pass it through. The teacher will be able to feel which
          sentences passed through and which ones did not, even when they cannot
          point at the line. So will you, when they ask.
        </p>
      </div>
    </Section>
  );
}

const KIT = [
  {
    n: '01',
    label: 'Claim list',
    accent: 'emerald' as const,
    body: 'Every claim in your draft (names, numbers, dates, quotes) listed once. Next to each: the source you opened, the page or section, and what the source actually says. If you cannot fill that row, the claim either gets cut or gets a real source.',
  },
  {
    n: '02',
    label: 'Paragraph reasons',
    accent: 'amber' as const,
    body: 'Every paragraph in your draft listed by its first three words. Next to each: one sentence on why it is in the piece. If you cannot say why, the paragraph is decoration, not argument. Lesson 4.2 said cut it. This is the audit.',
  },
  {
    n: '03',
    label: 'AI moves',
    accent: 'emerald' as const,
    body: 'Every AI suggestion you accepted, in one line. Next to each: why you accepted it. “Better rhythm.” “Tighter.” “Caught a vague word.” If you cannot say why, you did not decide. You let AI decide. That is the kind of acceptance that comes back later.',
  },
];

function KitGrid() {
  return (
    <div className="mt-6 space-y-4">
      {KIT.map((k) => {
        const isEmerald = k.accent === 'emerald';
        const borderCls = isEmerald ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEmerald ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEmerald ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={k.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">{k.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{k.label}</h4>
            </div>
            <p className="mt-3 text-text/90">{k.body}</p>
          </div>
        );
      })}
    </div>
  );
}

const QUESTIONS = [
  {
    n: 'Q1',
    label: 'Where did you get this?',
    body: 'About any specific claim. Answer: the source you opened, what page, and what the source actually says. “The article said” is not an answer. The article is.',
  },
  {
    n: 'Q2',
    label: 'Why is this paragraph here?',
    body: 'About any paragraph. Answer: the one sentence from your paragraph-reasons list. If you cannot say it without thinking for ten seconds, the paragraph does not have a reason yet.',
  },
  {
    n: 'Q3',
    label: 'What would you change?',
    body: 'About the whole piece, after the teacher has read it. Answer: the one thing you already know is weakest and would fix if you had another day. The students who know this earn more credit than the students who say “nothing.”',
  },
];

function QuestionsGrid() {
  return (
    <div className="mt-6 grid gap-4 md:grid-cols-3">
      {QUESTIONS.map((q) => (
        <div key={q.n} className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            {q.n} · {q.label}
          </p>
          <p className="mt-3 text-sm text-text/85">{q.body}</p>
        </div>
      ))}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Spot the indefensible" title="Three paragraphs. Three questions on each. Pick the one the writer cannot defend.">
      <p className="mt-6 text-text/90">
        For each paragraph, three follow-up questions are offered. Two of them
        the writer can answer with what is on the page. One they cannot. Pick
        the one that breaks the defense.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <DefenseDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  paragraph: string;
  questions: { key: string; text: string }[];
  correct: string;
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    paragraph:
      'The new school phone policy has cut classroom disruptions by roughly 40 percent according to a recent study, and students report feeling more focused during lessons. Teachers at our school have noticed the difference as well.',
    questions: [
      { key: 'a', text: 'Which recent study? Cite the author and year.' },
      { key: 'b', text: 'How is “classroom disruption” defined in the study you are citing?' },
      { key: 'c', text: 'Does your school have a phone policy?' },
    ],
    correct: 'a',
    verdict:
      'Option A. The paragraph names a number (40 percent) and a vague source (“a recent study”). The writer can probably answer C from memory. B is a hard question but at least the writer chose to use the term. A is the one that exposes whether a real source was opened. If the answer is “I do not remember which study,” the paragraph is not defensible and the number gets cut or replaced. The pattern: the easiest follow-up to ask is often the hardest to answer when AI wrote the sentence.',
  },
  {
    n: '02',
    paragraph:
      'My summer job at the deli changed how I think about work. I learned that small acts of attention matter more than big gestures. By the end of the summer I knew every regular customer’s order before they reached the counter.',
    questions: [
      { key: 'a', text: 'Name one regular and their order.' },
      { key: 'b', text: 'How long was the summer?' },
      { key: 'c', text: 'What do you mean by “small acts of attention”?' },
    ],
    correct: 'c',
    verdict:
      'Option C. The writer can name regulars and dates: those are particulars from their own life. The phrase “small acts of attention” is the AI-generic line in an otherwise specific paragraph. The student probably did not write that phrase. They cannot define it on the spot because there is no specific thing behind it. The fix is the lesson 4.3 move: replace the phrase with a real instance from the summer. “Frank never said hello. He nodded. I started nodding back the second week.”',
  },
  {
    n: '03',
    paragraph:
      'Artificial intelligence is transforming society in profound ways. It impacts every aspect of how we live, work, and learn. Governments, businesses, and individuals all have a role to play in shaping its future. The decisions we make now will define the years ahead.',
    questions: [
      { key: 'a', text: 'What is the through-line of this paragraph?' },
      { key: 'b', text: 'Is AI actually transforming society?' },
      { key: 'c', text: 'Which decision, made by whom, are you actually writing about?' },
    ],
    correct: 'a',
    verdict:
      'Option A. B is a softball. C is gettable: the writer could pick one. A is the killer because the paragraph has no through-line at all. It is four general statements stacked. The writer cannot defend why the paragraph exists or what it adds because the answer is “it does not.” Extra sting: the paragraph is about AI and was almost certainly written by AI. This is the failure mode lesson 4.2 was built to catch and lesson 4.4 is the moment it gets called out. Cut the paragraph or rewrite it around one specific decision your specific reader actually has to make.',
  },
];

function DefenseDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<string | null>(null);
  const correctQuestion = drill.questions.find((s) => s.key === drill.correct);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The paragraph
      </p>
      <p className="mt-3 text-text/90 italic">&ldquo;{drill.paragraph}&rdquo;</p>
      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which question can the writer NOT defend?
        </p>
        <div className="mt-3 space-y-2">
          {drill.questions.map((q) => {
            const isPicked = picked === q.key;
            const isCorrect = q.key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'w-full rounded-md border border-line bg-elevated p-3 text-left text-sm text-text/85 hover:border-amber';
            if (shouldShowCorrect)
              cls =
                'w-full rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-3 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'w-full rounded-md border border-rose-500 bg-rose-500/[0.08] p-3 text-left text-sm text-text';
            return (
              <button
                key={q.key}
                className={cls}
                onClick={() => setPicked(q.key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Question {q.key.toUpperCase()}
                </span>
                <span
                  className="mt-1 block"
                  dangerouslySetInnerHTML={{ __html: q.text }}
                />
              </button>
            );
          })}
        </div>
        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Question {picked?.toUpperCase()}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That question the writer can answer. The one that breaks the
              defense is below.
            </p>
          </div>
        )}
        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              Breaks the defense · Question {drill.correct.toUpperCase()}
            </p>
            <p
              className="mt-2 text-sm text-text/80 italic"
              dangerouslySetInnerHTML={{ __html: `“${correctQuestion?.text ?? ''}”` }}
            />
            <p
              className="mt-3 text-sm text-text/90"
              dangerouslySetInnerHTML={{ __html: drill.verdict }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define defensible in one sentence." answer="True, yours, and traceable: every claim points to a real source you opened, every paragraph has a reason, every AI suggestion you took has a why." />
        <QuickCheckItem n="Q2" q="Name the three pieces of the defense kit." answer="Claim list (every claim to a real source), paragraph reasons (every paragraph to a one-sentence reason), AI moves (every accepted AI suggestion to a why)." />
        <QuickCheckItem n="Q3" q="Name the three questions a teacher will ask." answer="Where did you get this. Why is this paragraph here. What would you change. Every other question is a variation of one of those." />
        <QuickCheckItem n="Q4" q="State the pass-through rule." answer="Every AI sentence that ends up in the final draft has to pass through your brain: read out loud, decide to keep, and re-type by hand. Pasting is not passing through." />
        <QuickCheckItem n="Q5" q="Difference between shippable and defensible, in one sentence." answer="Shippable means you would submit it. Defensible means you would still be okay an hour later when a teacher pulls you aside and asks why a sentence is there." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Run it before you turn anything in.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 4.4 · Lock card · Defend what you shipped
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Shippable means you would submit it. Defensible means you can still
          stand behind it when somebody asks why.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <KitTile n="01" name="Claim list" body="Every name, number, date, quote → the source you opened. If the row is empty, the claim is empty." />
          <KitTile n="02" name="Para reasons" body="Every paragraph → one sentence on why it is in the piece. Cannot say it = cut it." />
          <KitTile n="03" name="AI moves" body="Every AI suggestion you took → why you took it. No reason = AI decided, not you." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The three questions to be ready for
          </p>
          <ul className="mt-2 space-y-1.5 text-text/90">
            <li>
              <span className="font-mono text-xs text-amber mr-2">Q1</span>
              Where did you get this?
            </li>
            <li>
              <span className="font-mono text-xs text-amber mr-2">Q2</span>
              Why is this paragraph here?
            </li>
            <li>
              <span className="font-mono text-xs text-amber mr-2">Q3</span>
              What would you change?
            </li>
          </ul>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Standing behind your work is a habit. Build it in school and it follows
          you into every job that matters.
        </p>
      </div>
    </Section>
  );
}

function KitTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 4.5
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Week 4 capstone: care about who reads it
          </h3>
          <p className="mt-3 text-sm text-text/85">
            All five moves on one real piece. Pick a real reader: a sibling, a
            former teacher, a coach. Write 500 words they would actually want to
            read. Through-line, particulars, defense pass, ship.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 4.4 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p className="mt-3 border-t border-line pt-3 text-sm text-text/85">
          {answer}
        </p>
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    '3.4 + 4.3 review',
    'The standing move',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
